/**
 * Phase 1 migration driver: legacy files → SQLite, per workspace, on first
 * open with the sync_engine_v1 flag on.
 *
 * JS owns the legacy-format knowledge (loaders + normalization); Rust owns
 * the backup → import → verify → mark discipline (workspace_db_migrate_import
 * uses sync/backup.rs). A failed verify leaves the workspace on file storage
 * — originals are never mutated, the backup stays on disk, and the router
 * falls through to the legacy path.
 */
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { syncEngineEnabled } from '../utils/featureFlags';
import { isGitWorkspace } from '../utils/workspaceStorage';
import { loadSnapshot, normalizeSnapshot, snapshotDirAbsolute } from './localWorkspaceStore';
import { loadGitSnapshot } from './gitWorkspaceStore';

/** Stable SQLite key for a workspace (local id wins over remote id). */
export function workspaceDbKey(workspace) {
  return String(workspace?.localId || workspace?.id || '');
}

const migratedCache = new Map(); // workspaceId -> boolean
const inFlight = new Map(); // workspaceId -> Promise<boolean>

/**
 * True when SQLite is the active store for this workspace: enrolled in the
 * flag AND migrated (running the migration now if needed). Never throws —
 * any failure means "keep using files".
 */
export async function ensureWorkspaceMigrated(workspace) {
  if (!isTauri() || !syncEngineEnabled(workspace)) return false;
  const id = workspaceDbKey(workspace);
  if (!id) return false;
  if (migratedCache.get(id) === true) return true;
  if (inFlight.has(id)) return inFlight.get(id);

  const run = (async () => {
    try {
      if (await invoke('workspace_db_is_migrated', { workspaceId: id })) {
        migratedCache.set(id, true);
        return true;
      }
      const git = isGitWorkspace(workspace);
      const raw = git ? await loadGitSnapshot(workspace.rootPath) : await loadSnapshot(id);
      // Canonical keys only: the Rust verify step deep-compares its re-load
      // against exactly what we pass, so loader extras (id, storage, ...)
      // must not leak in.
      const snapshot = normalizeSnapshot(raw);
      // Git workspaces: YAML lives in the user's repo and import never
      // mutates it — nothing to back up.
      const backupSource = git ? null : await legacySnapshotDirAbs(id);
      const backupPath = await invoke('workspace_db_migrate_import', {
        workspaceId: id,
        snapshot,
        backupSource,
      });
      if (backupPath) {
        console.info(`[sync] workspace ${id} migrated to SQLite (backup: ${backupPath})`);
      }
      migratedCache.set(id, true);
      return true;
    } catch (err) {
      console.warn(`[sync] workspace ${id} migration failed — staying on file storage:`, err);
      migratedCache.set(id, false);
      return false;
    } finally {
      inFlight.delete(id);
    }
  })();
  inFlight.set(id, run);
  return run;
}

/** Test/debug hook: drop cached results so the next call re-checks Rust. */
export function resetMigrationCache() {
  migratedCache.clear();
}

async function legacySnapshotDirAbs(id) {
  // snapshotDirAbsolute() carries the account namespace segment, so the backup
  // source is always the CURRENT account's copy of this workspace.
  return snapshotDirAbsolute(id);
}
